import { type Component, createApp } from 'vue'
import App from './App.vue';
import router from './ts/router';
import store from './ts/store';
import i18n from '@/ts/i18n';
import '@/ts/debug';

import L from 'leaflet';
import {type State} from  '@/ts/store';

import 'leaflet-rotate';

// @ts-ignore
import { registerSW } from 'virtual:pwa-register';
if (import.meta.env.VITE_PWA) {
    registerSW({ immediate: true })
}

import Place from '@/ts/Place';

import 'leaflet-hotline';
import 'Leaflet.MultiOptionsPolyline';
import 'leaflet/dist/leaflet.css';
import 'leaflet.motion/dist/leaflet.motion.min';
import 'leaflet-contextmenu';
import 'leaflet-contextmenu/dist/leaflet.contextmenu.css';
import './ts/L.CanvasLayer';
import { Store } from 'vuex';
import { DataOrigin } from './ts/types';
import NoSleep from 'nosleep.js';
import { type ControlSaveTiles } from './ts/leafletoffline';


interface WindowLeaflet {
    markers: {[key: number]: {[key: number]: L.Marker | L.CircleMarker}}
}

interface WindowExternalPlaces {
  czaswlas: Place[],
  komoothighlight: Place[],
  komootother: Place[],
  komoottrailview: Place[],
  komoottour: Place[],
}

interface TrackLeafletObjects {
  mapTracks: L.Polyline[],
  animateTracks: L.Polyline[],
  plannedMarkers: L.Marker[],
  middleMarkers: L.Marker[],
  startMarker: L.Marker,
  finishMarker: L.Marker,
}

declare global {
    interface Window {
      apiHost: string;
      mediaHost: string;
      leaflet: WindowLeaflet;
      isDesktop: boolean;
      isPwa: boolean;
      VITE_BUILD_DATE: string;
      opfsRoot: FileSystemDirectoryHandle;
      mediaDirHandle: FileSystemDirectoryHandle;
      gridDirHandle: FileSystemDirectoryHandle;
      mainmap: L.Map;
      externalPlaces: WindowExternalPlaces;
      $store: Store<State>;
      tracks: {[key: number]: TrackLeafletObjects};
      worker: Worker;
      detailsX: number;
      detailsY: number;
      detailsLastZIndex: number;
      unique: number;
      getUnique: () => number;
      lastModal: bootstrap.Modal;
      gridDebugLayer: GridDebugLayer;
      activeLayer: L.TileLayer;
      noSleep: NoSleep;
      offlineControl: ControlSaveTiles;
      paletteIndex: number;
      paletteMin: number;
      paletteMax: number;
      paletteMap: any[];
      layerControl: L.Control.Layers;
      areas: {[key: string]: [[number, number], [number, number]]};
      selectedArea: string;
      debugEnabled: boolean;
      debugMessages: string[];
    }
}

window.areas = {
    'PUSZCZA_NOTECKA': [[52.60444, 15.42921], [52.87802, 16.98279]],
    'PUSZCZA_BYDGOSKA': [[52.83056, 17.88215], [53.11460, 18.75272]],
    'PUSZCZA_ZIELONKA': [[52.466, 16.977], [52.640, 17.240]],
    'BORY1': [[53.44593, 16.79063], [54.14647, 18.74028]],
    'POLSKA': [[49, 14], [54.5, 24.1]],
    'POLSKA_NW': [[52.33, 14.68], [54.34, 19.13]],
    'POLSKA_NW_OSM': [[51.7, 15], [54.2, 19.2]],
    'SWIETNO': [[51.9413, 15.82649], [52.09174, 16.17702]],
    'CHODZIEZ': [[52.84011, 16.65802], [53.03667, 16.98967]],
    'KARKONOSZE': [[50.72776, 15.25932], [50.85884, 15.83885]],
    'JASZCZEREK': [[53.548, 18.241], [53.788, 18.700]],
    'POLNICA': [[53.737746, 17.311707], [53.865486, 17.633743]],
    'TRZCIEL': [[52.396, 15.745], [52.486, 15.949]],
    'DRAWIENSKI': [[52.848, 15.743], [53.176, 16.065]],
    'POZNAN': [[52.200, 16.747], [52.672, 16.981]],
    'TORUN_BYDGOSZCZ': [[52.894, 17.872], [53.206, 18.723]],
}


window.leaflet = {'markers': {}}

// @ts-ignore
for(const dataOrigin of Object.values(DataOrigin).filter(value => typeof value === 'number')) {
  window.leaflet.markers[dataOrigin] = {};
}

window.externalPlaces = {'czaswlas': [], 'komoothighlight': [], 'komoottour': [], 'komoottrailview': [], 'komootother': []};
window.tracks = {};
window.apiHost = window.location.hostname == 'localhost' ? 'http://localhost:8000/djangoapp/' : '/djangoapp/';
window.mediaHost = window.location.hostname === 'localhost' ? 'http://localhost:8000/media/' : '/media/';
window.isDesktop = !import.meta.env.VITE_PWA;
window.isPwa = import.meta.env.VITE_PWA;
window.VITE_BUILD_DATE = import.meta.env.VITE_BUILD_DATE;
window.noSleep = new NoSleep();
window.paletteIndex = 0;
window.paletteMin = 20;
window.paletteMax = 105;

import 'bootstrap';
import 'bootstrap/dist/css/bootstrap.css';

import {createBootstrap, Components, Directives} from 'bootstrap-vue-next'

import 'bootstrap-styles/css/bootstrap.css'
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css'

import '@/assets/css/flags.css';
import '@/assets/css/global.css';

//const myWorker = new Worker("./worker-opfs.js");
//const myWorker = new Worker("./worker-sqlite3.js", {type: 'module'});
const myWorker = new Worker(
  new URL('./worker-sqlite3.js', import.meta.url),
  { type: 'module' }
);
window.worker = myWorker;

import AppTrack from '@/components/AppTrack.vue';
import AppTrackGallery from '@/components/AppTrackGallery.vue';
import AppTrackGroup from '@/components/AppTrackGroup.vue';
import TrackStatusIcon from '@/components/TrackStatusIcon.vue';
import TrackTypeIcon from '@/components/TrackTypeIcon.vue';
import TrackDownload from '@/components/TrackDownload.vue';
import InfoModal from '@/components/InfoModal.vue';
import ColorPopover from '@/components/ColorPopover.vue';
import ObjectsTab from '@/components/ObjectsTab.vue';
import MapPlace from '@/components/MapPlace.vue';
import PlannedTracks from '@/components/PlannedTracks.vue';
import TrackDetails from '@/components/TrackDetails.vue';
import OfflineCard from '@/components/OfflineCard.vue';
import AppArea from '@/components/AppArea.vue';
import Areas from '@/components/Areas.vue';
import SaveTrackModal from '@/components/SaveTrackModal.vue';
import SavePlaceModal from '@/components/SavePlaceModal.vue';
import PlaceTypeGroupCard from '@/components/PlaceTypeGroupCard.vue';
import CzasWLasCard from '@/components/CzasWLasCard.vue';
import OpenStreetMapPointsCard from '@/components/OpenStreetMapPointsCard.vue';
import KomootTrailViewCard from '@/components/KomootTrailViewCard.vue';
import KomootHighlightPoiCard from '@/components/KomootHighlightPoiCard.vue';
import GSMCard from '@/components/GSMCard.vue';
import KomootToursCard from '@/components/KomootToursCard.vue';
import StravaSegmentsCard from '@/components/StravaSegmentsCard.vue';
import BDLCard from '@/components/BDLCard.vue';
import DPozCard from '@/components/DPozCard.vue';
import Spinner from '@/components/Spinner.vue';
import CacheTableRow from '@/components/CacheTableRow.vue';
import DebugCard from '@/components/cards/Debug.vue';
import VisitedTilesCard from '@/components/cards/VisitedTiles.vue';
import ContourCard from '@/components/cards/Contour.vue';

window.detailsX = 50;
window.detailsY = 50;
window.detailsLastZIndex = 1;

window.unique = 0;

window.getUnique = function() {
  window.unique = window.unique + 1;
  return window.unique;
};

const app = createApp(App)

app.component('AppTrack', AppTrack);
app.component('AppTrackGallery', AppTrackGallery);
app.component('AppTrackGroup', AppTrackGroup);
app.component('TrackStatusIcon', TrackStatusIcon);
app.component('TrackTypeIcon', TrackTypeIcon);
app.component('TrackDownload', TrackDownload);
app.component('InfoModal', InfoModal);
app.component('ColorPopover', ColorPopover);
app.component('ObjectsTab', ObjectsTab);
app.component('MapPlace', MapPlace);
app.component('PlannedTracks', PlannedTracks);
app.component('TrackDetails', TrackDetails);
app.component('OfflineCard', OfflineCard);
app.component('AppArea', AppArea);
app.component('Areas', Areas);
app.component('SaveTrackModal', SaveTrackModal);
app.component('SavePlaceModal', SavePlaceModal);
app.component('PlaceTypeGroupCard', PlaceTypeGroupCard);
app.component('CzasWLasCard', CzasWLasCard);
app.component('OpenStreetMapPointsCard', OpenStreetMapPointsCard);
app.component('KomootTrailViewCard', KomootTrailViewCard);
app.component('KomootHighlightPoiCard', KomootHighlightPoiCard);
app.component('GSMCard', GSMCard);
app.component('KomootToursCard', KomootToursCard);
app.component('StravaSegmentsCard', StravaSegmentsCard);
app.component('BDLCard', BDLCard);
app.component('DPozCard', DPozCard);
app.component('Spinner', Spinner);
app.component('CacheTableRow', CacheTableRow);
app.component('DebugCard', DebugCard);
app.component('VisitedTilesCard', VisitedTilesCard);
app.component('ContourCard', ContourCard);
// @ts-ignore
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
app.component('VueSelect', vSelect);

import 'lightgallery/css/lightgallery-bundle.css';

import { Sketch } from '@ckpack/vue-color';
import type { GridDebugLayer } from './ts/GridDebugLayer';
app.component('SketchPicker', Sketch);

app.use(router)
app.use(store)
app.use(i18n)

store.commit("initialize");

window.$store = store;

app.use(
  createBootstrap()
)
for (const name in Components) {
  app.component(name, Components[name as keyof typeof Components] as Component)
}
for (const name in Directives) {
  app.directive(name.replace(/^v/, ''), Directives[name as keyof typeof Directives])
}
// @ts-ignore
app.config.globalProperties.$DataOrigin = DataOrigin;

app.mount('#app')
